import React, { useEffect, useRef } from 'react';
import { Package, TestTubes, LineChart, Unlock, Clock, CalendarCheck, Users, RefreshCw, TrendingUp, Repeat, Hash, Building2, UserCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import kiwiCapeLogo from '@/assets/kiwi-cape-logo.svg';
const steps = [{
  icon: <Hash className="w-6 h-6" />,
  title: "Define Study Cohorts",
  date: "",
  description: "Select how many testers to sponsor with a CGM device",
  controlledBy: "customer"
}, {
  icon: <Package className="w-6 h-6" />,
  title: "Send Test Products",
  date: "Kick-off",
  description: "Ship your products to our facility for distribution",
  controlledBy: "customer"
}, {
  icon: <Unlock className="w-6 h-6" />,
  title: "Publish Your Experiment",
  date: "Real-time",
  description: "Login to Tastermonial Lab to publish your experiment protocols to our app users",
  controlledBy: "tastermonial"
}, {
  icon: <Users className="w-6 h-6" />,
  title: "Recruit Testers",
  date: "2-8 weeks",
  description: "We support recruitment through our CGM tester pool alongside your own outreach — you choose committed participants.",
  controlledBy: "customer",
  isLoopStart: true
}, {
  icon: <TestTubes className="w-6 h-6" />,
  title: "Experiment Boxes Distribution",
  date: "2 weeks",
  description: "We handle experiment logistics — assembling and shipping your co-branded boxes to selected testers",
  controlledBy: "tastermonial",
  isLoopStart: true
}, {
  icon: <LineChart className="w-6 h-6" />,
  title: "Testing Begins",
  date: "2–6+ weeks",
  description: "Participants enroll in your experiment through the Tastermonial app, submit results, and receive rewards, with optional video feedback; timelines vary based on experiment duration and participant start times.",
  controlledBy: "customer",
  isLoopStart: true
}, {
  icon: <CalendarCheck className="w-6 h-6" />,
  title: "Access Live Results",
  date: "Real-time",
  description: "Explore a unified dataset combining glycemic metrics, participant data, and real-world feedback",
  controlledBy: "tastermonial",
  isLoopEnd: true
}];
const HowItWorks: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.animate-on-scroll');
          elements.forEach((el, i) => {
            setTimeout(() => {
              el.classList.add('visible');
            }, i * 150);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} id="how-it-works" className="section-padding bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-12 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            How it Works
          </h2>
          <p className="text-neutral-700 text-lg">
            Put your product to test for sugar spikes and access data in real-time
          </p>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-8 mb-10 animate-on-scroll">
          <div className="flex items-center gap-2">
            <div className="w-3 h-8 rounded-full bg-primary"></div>
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <img src={kiwiCapeLogo} alt="Tastermonial" className="w-6 h-6" />
              <span>Tastermonial handles</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-8 rounded-full bg-amber-400"></div>
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <UserCircle className="w-4 h-4" />
              <span>Your action</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-2xl mx-auto">
          {/* Vertical timeline connector */}
          <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-neutral-200 z-0"></div>

          {/* Loop indicator - curved arrow on the right side */}
          <div className="hidden md:block absolute -right-16 top-[calc(33%)] bottom-[calc(10%)] w-12">
            <svg className="w-full h-full text-primary/30" viewBox="0 0 48 200" fill="none" preserveAspectRatio="none">
              <path d="M 4 0 Q 44 0 44 40 L 44 160 Q 44 200 4 200" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" fill="none" />
              <path d="M 4 195 L 12 200 L 4 205" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
            <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-2">
              <RefreshCw className="w-5 h-5 text-primary/50" />
            </div>
          </div>

          <div className="flex flex-col gap-8 relative z-10">
            {steps.map((step, index) => <div key={index} className="flex items-start gap-6 animate-on-scroll">
                {/* Side stripe indicator */}
                <div className={`absolute left-0 w-1 rounded-full ${step.controlledBy === "tastermonial" ? "bg-primary" : "bg-amber-400"}`} style={{
              height: '56px',
              marginTop: '0'
            }}></div>
                
                <div className={`flex-shrink-0 w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white shadow-md relative z-10 ${step.isLoopStart || step.isLoopEnd ? 'ring-2 ring-primary/30 ring-offset-2' : ''}`}>
                  {step.icon}
                </div>

                <div className="flex-1 pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{step.title}</h3>
                    {step.date && <div className="flex items-center gap-1.5">
                        <div className={`px-2 py-1 text-white text-xs font-semibold rounded-full ${step.date === "Real-time" || step.date === "2 weeks" ? "bg-purple-500" : "bg-[#F59E0B]"}`}>
                          {step.date}
                        </div>
                        {(step.date === "Real-time" || step.date === "2 weeks") && <img src={kiwiCapeLogo} alt="Tastermonial guaranteed" className="w-6 h-6" />}
                      </div>}
                    {step.isLoopStart && <div className="hidden md:flex items-center text-primary/60">
                        <Repeat className="w-4 h-4" />
                      </div>}
                  </div>
                  <p className="text-neutral-700">{step.description}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Iterative cycle visual card */}
        <Card className="mt-12 max-w-xl mx-auto animate-on-scroll overflow-hidden border-primary/20 border-2 border-dashed bg-primary/5">
          <div className="p-6">
            <div className="flex items-center justify-center gap-8">
              {/* Small cycle visualization */}
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <TrendingUp className="w-5 h-5 text-primary/40" />
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-primary" />
                </div>
                <RefreshCw className="w-5 h-5 text-primary/40" />
                <div className="w-12 h-12 rounded-full bg-primary/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <TrendingUp className="w-5 h-5 text-primary/40" />
                <div className="w-12 h-12 rounded-full bg-primary/30 flex items-center justify-center">
                  <CalendarCheck className="w-6 h-6 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Enhanced results timeline card */}
        <Card className="mt-6 max-w-3xl mx-auto animate-on-scroll overflow-hidden bg-gradient-to-r from-neutral-50 to-neutral-100 border border-neutral-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <CalendarCheck className="w-6 h-6 text-primary mr-2" />
              <h4 className="text-xl font-semibold">Results Timeline</h4>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-10 h-10 text-primary" />
              </div>

              <p className="text-neutral-700 text-center md:text-left">Once we receive your samples and you publish your experiment protocol, allow  2 weeks for kit preparation. Early results begin arriving as testers start, while recruitment continues. Depending on participant timing, studies typically complete in 4–8 weeks.<span className="font-semibold">10 weeks</span>~2 weeks for kit preparation. Early results begin arriving as testers start, while recruitment continues. Depending on participant timing, studies typically complete in ~4–8 weeks. <span className="font-semibold">2 weeks</span> for kit preparation and <span className="font-semibold">8 weeks</span> for testing and validation.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </section>;
};
export default HowItWorks;